<template>
  <Teleport to="#navbarEnd">
    <p>{{ character.gold }} gold</p>
  </Teleport>

  <div data-view="createCharacter">
    <div class="columns is-variable is-6">
      <div class="column is-one-third-widescreen is-half-desktop is-one-quarter-fullhd">
        <Character :character="character" :computed="computed" />
      </div>
      <div class="column">

        <!-- Name -->
        <section v-if="currentSection === 0" class="block">
          <h1 class="sectionTitle title is-2">Introduce yourself</h1>
          <div class="content">
            <div class="field">
              <div class="control">
                <input v-model="character.name" class="input" type="text" placeholder="— Choose your name —" @keydown="onReturnPressed($event, character.name, () => currentSection++)">
              </div>
            </div>
          </div>
          <div class="field is-grouped">
            <div class="control">
              <button class="button is-primary" @click="currentSection++" :disabled="!character.name ? true : null">Next</button>
            </div>
            <div class="control">
              <RouterLink :to="{ name: 'listCharacters' }" class="button is-primary is-light">Nevermind</RouterLink>
            </div>
          </div>
        </section>

        <!-- Race -->
        <section v-if="currentSection === 1" class="block">
          <h1 class="sectionTitle title is-2">Choose your race</h1>
          <div class="content block">
            <div class="field">
              <div class="control">
                <div class="select is-wide">
                  <select v-model="character.race">
                    <option v-for="(race, key) in raceList" :key="key" :value="key" :selected="race === key" :disabled="key === ''">
                      {{ key === '' ? '— Choose your race —' : race.name }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          
          <template v-if="race.features && race.features.length">
            <p class="label" style="margin-bottom: 1.5rem">{{ race.name }} features</p>
            <div class="content block">
              <CardGroup>
                <template v-for="feature in race.features" :key="feature">
                  <FeatureCard v-if="featureList[feature].type === 'feature'" :feature="feature" />
                  <ActionCard v-else :feature="feature" />
                </template>
              </CardGroup>
            </div>
          </template>

          <template v-if="race.featureChoices && race.featureChoices.length">
            <template v-for="(group, i) in race.featureChoices" :key="i">
              <p class="label" style="margin-bottom: 1.5rem">{{ group.name }} (choose {{ numFeaturesSelected(group.features) }} of {{ group.numChoices }})</p>
              <div class="content block">
                <CardGroup>
                  <template v-for="feature in group.features" :key="feature">
                    <FeatureCard 
                      v-if="featureList[feature].type === 'feature'" 
                      :feature="feature"
                      :checkbox="true"
                      :true-value="feature"
                      :value="character.features"
                      :disabled="!character.features.includes(feature) && numFeaturesSelected(group.features) >= group.numChoices"
                      @changed="addRemoveFeature($event, feature)"
                    />
                    <ActionCard 
                      v-else 
                      :feature="feature" 
                      :checkbox="true"
                      :true-value="feature"
                      :value="character.features"
                      :disabled="!character.features.includes(feature) && numFeaturesSelected(group.features) >= group.numChoices"
                      @changed="addRemoveFeature($event, feature)"
                    />
                  </template>
                </CardGroup>
              </div>
            </template>
          </template>

          <div class="field is-grouped">
            <div class="control">
              <button class="button is-primary" @click="currentSection++" :disabled="!character.race || numTotalRaceFeatures !== character.features.length">Next</button>
            </div>
            <div class="control">
              <button class="button is-primary is-light" @click="currentSection--">Back</button>
            </div>
          </div>
        </section>

        <!-- Class -->
        <section v-if="currentSection === 2" class="block">
          <h1 class="sectionTitle title is-2">Choose your class</h1>
          <div class="content">
            <div class="field">
              <div class="control">
                <div class="select is-wide">
                  <select v-model="character.className">
                    <option v-for="(classObj, key) in classList" :key="key" :value="key" :selected="character.className === key" :disabled="key === 'adventurer'">
                      {{ key === 'adventurer' ? '— Choose your class —' : classObj.name }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <template v-if="characterClass.features && characterClass.features.length">
            <p class="label" style="margin-bottom: 1.5rem">{{ className }} features</p>
            <div class="content block">
              <CardGroup>
                <template v-for="feature in characterClass.features" :key="feature">
                  <FeatureCard v-if="featureList[feature].type === 'feature'" :feature="feature" />
                  <ActionCard v-else :feature="feature" />
                </template>
              </CardGroup>
            </div>
          </template>

          <template v-if="characterClass.featureChoices && characterClass.featureChoices.length">
            <template v-for="(group, i) in characterClass.featureChoices" :key="i">
              <p class="label" style="margin-bottom: 1.5rem">{{ group.name }} (choose {{ numFeaturesSelected(group.features) }} of {{ group.numChoices }})</p>
              <div class="content block">
                <CardGroup>
                  <template v-for="feature in group.features" :key="feature">
                    <FeatureCard 
                      v-if="featureList[feature].type === 'feature'" 
                      :feature="feature"
                      :checkbox="true"
                      :true-value="feature"
                      :value="character.features"
                      :disabled="!character.features.includes(feature) && numFeaturesSelected(group.features) >= group.numChoices"
                      @changed="addRemoveFeature($event, feature)"
                    />
                    <ActionCard 
                      v-else 
                      :feature="feature" 
                      :checkbox="true"
                      :true-value="feature"
                      :value="character.features"
                      :disabled="!character.features.includes(feature) && numFeaturesSelected(group.features) >= group.numChoices"
                      @changed="addRemoveFeature($event, feature)"
                    />
                  </template>
                </CardGroup>
              </div>
            </template>
          </template>

          <div class="field is-grouped">
            <div class="control">
              <button class="button is-primary" @click="hasSpells ? currentSection++ : currentSection += 2" :disabled="character.className === 'adventurer' || numTotalClassFeatures !== character.features.length">Next</button>
            </div>
            <div class="control">
              <button class="button is-primary is-light" @click="currentSection--">Back</button>
            </div>
          </div>
        </section>

        <!-- Spells -->
        <section v-if="currentSection === 3" class="block">
          <h1 class="sectionTitle title is-2">Choose your spells</h1>

          <template v-if="characterClass.spells && characterClass.spells?.length">
            <p class="label" style="margin-bottom: 1.5rem">{{ className }} spells and cantrips</p>
            <div class="content block">
              <CardGroup>
                <template v-for="spell in characterClass.spells" :key="spell">
                  <SpellCard :spell="spell" />
                </template>
              </CardGroup>
            </div>
          </template>

          <template v-if="characterClass.spellChoices && Object.keys(characterClass.spellChoices).length">
            <template v-for="(group, level) in characterClass.spellChoices" :key="level">
              <p class="label" style="margin-bottom: 1.5rem">{{ Number(level) === 0 ? 'Cantrips' : `Level ${level} spells` }} (choose {{ numSpellsSelected(group.spells) }} of {{ group.numChoices }})</p>
              <div class="content block">
                <CardGroup>
                  <template v-for="spell in group.spells" :key="spell">
                    <SpellCard 
                      :spell="spell"
                      :checkbox="true"
                      :true-value="spell"
                      :value="character.spells"
                      :disabled="!character.spells.includes(spell) && numSpellsSelected(group.spells) >= group.numChoices"
                      @changed="addRemoveSpell($event, spell)"
                    />
                  </template>
                </CardGroup>
              </div>
            </template>
          </template>

          <div class="field is-grouped">
            <div class="control">
              <button class="button is-primary" @click="currentSection++" :disabled="character.spells.length !== numTotalSpells">Next</button>
            </div>
            <div class="control">
              <button class="button is-primary is-light" @click="currentSection--">Back</button>
            </div>
          </div>
        </section>

        <!-- Equipment -->
        <section v-if="currentSection === 4" class="block">
          <h1 class="sectionTitle title is-2">Buy your equipment</h1>
          
          <p class="label" style="margin-bottom: 1.5rem">Weapons (choose {{ mainHandRef === null ? 0 : 1 }} of 1)</p>
          <div class="content block">
            <CardGroup>
              <template v-for="weapon in mainHandWeaponChoices" :key="weapon">
                <WeaponCard 
                  :weapon="weapon"
                  :checkbox="true"
                  :true-value="weapon"
                  :value="mainHandRef === weapon"
                  :disabled="mainHandRef !== weapon && (character.gold < weaponList[weapon].price || mainHandRef !== null)"
                  @changed="mainHandRef = ($event ? weapon : null)"
                />
              </template>
            </CardGroup>
          </div>

          <template v-if="offHandWeaponChoices.length">
            <p class="label" style="margin-bottom: 1.5rem">Off-hand weapon (choose {{ offHandRef === null ? 0 : 1 }} of {{ mainHandWeaponIsTwoHand ? 0 : 1 }})</p>
            <div class="content block">
              <CardGroup>
                <template v-for="weapon in offHandWeaponChoices" :key="weapon">
                  <EquipmentCard 
                    v-if="weaponList[weapon].armor"
                    :equipment="weapon"
                    :checkbox="true"
                    :true-value="weapon"
                    :value="offHandRef === weapon"
                    :disabled="offHandRef !== weapon && (character.gold < weaponList[weapon].price || mainHandWeaponIsTwoHand || offHandRef !== null)"
                    @changed="offHandRef = ($event ? weapon : null)"
                  />
                  <WeaponCard
                    v-else
                    :weapon="weapon"
                    :checkbox="true"
                    :true-value="weapon"
                    :value="offHandRef === weapon"
                    :disabled="offHandRef !== weapon && (character.gold < weaponList[weapon].price || mainHandWeaponIsTwoHand || offHandRef !== null)"
                    @changed="offHandRef = ($event ? weapon : null)"
                  />
                </template>
              </CardGroup>
            </div>
          </template>

          <template v-if="armorChoices.length">
            <p class="label" style="margin-bottom: 1.5rem">Armor (choose {{ armorRef === null ? 0 : 1 }} of 1)</p>
            <div class="content block">
              <CardGroup>
                <template v-for="armor in armorChoices" :key="armor">
                  <EquipmentCard
                    :equipment="armor"
                    :checkbox="true"
                    :true-value="armor"
                    :value="armorRef === armor"
                    :disabled="armorRef !== armor && (character.gold < equipmentList[armor].price || armorRef !== null)"
                    @changed="addRemoveArmor($event, armor)"
                  />
                </template>
              </CardGroup>
            </div>
          </template>

          <template v-if="difficulty.itemChoices.length">
            <p class="label" style="margin-bottom: 1.5rem">Items</p>
            <div class="content block">
              <CardGroup>
                <template v-for="item in difficulty.itemChoices" :key="item">
                  <EquipmentCard 
                    :equipment="item"
                    :checkbox="true"
                    :true-value="item"
                    :value="character.equipment"
                    :disabled="!character.equipment.includes(item) && character.gold < calcPrice(equipmentList[item])"
                    @changed="addRemoveItem($event, item)"
                  />
                </template>
              </CardGroup>
            </div>
          </template>

          <div class="field is-grouped">
            <div class="control">
              <button class="button is-primary" @click="currentSection++">Next</button>
            </div>
            <div class="control">
              <button class="button is-primary is-light" @click="hasSpells ? currentSection-- : currentSection -= 2">Back</button>
            </div>
          </div>
        </section>
        
        <!-- Skills -->
        <section v-if="currentSection === 5" class="block">
          <h1 class="sectionTitle title is-2">Choose your skills</h1>
          <div class="content">
            <div class="field">
              <label class="label">Skills (choose {{ character.skills.length }} of {{ characterClass.numSkills }})</label>
            </div>
            <div v-for="(skill, key) in skillChoices" :key="key" class="field">
              <div class="control">
                <label v-if="skill.included" class="checkbox" :disabled="true">
                  <input :checked="true" type="checkbox" :disabled="true">
                  {{ skill.name }} <span>({{ skill.ability }})</span>
                </label>
                <label v-else class="checkbox" :disabled="((character.skills.length >= characterClass.numSkills) && !character.skills.includes(key)) ? true : undefined">
                  <input v-model="character.skills" :id="key" :value="key" type="checkbox" :disabled="(character.skills.length >= characterClass.numSkills) && !character.skills.includes(key)">
                  {{ skill.name }} <span>({{ skill.ability }})</span>
                </label>
              </div>
            </div>
          </div>

          <div class="field is-grouped">
            <div class="control">
              <button class="button is-primary" @click="currentSection++" :disabled="character.skills.length !== characterClass.numSkills">Next</button>
            </div>
            <div class="control">
              <button class="button is-primary is-light" @click="currentSection--">Back</button>
            </div>
          </div>
        </section>
        
        <!-- Notes -->
        <section v-if="currentSection === 6" class="block">
          <h1 class="sectionTitle title is-2">Character Notes</h1>
          <div class="content">
            <div class="field">
              <label class="label">Write some notes about your character (optional)</label>
            </div>
            <div class="field">
              <div class="control">
                <textarea class="textarea" rows="10" v-model="character.notes" @keydown="onReturnPressed($event, true, () => createCharacter())" />
              </div>
            </div>
          </div>

          <div class="field is-grouped">
            <div class="control">
              <button class="button is-primary" @click="createCharacter">Create Character</button>
            </div>
            <div class="control">
              <button class="button is-primary is-light" @click="currentSection--">Back</button>
            </div>
          </div>
        </section>

      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, provide, reactive, watch } from 'vue'
import { useRouter } from 'vue-router'
import uniqid from 'uniqid'

// Hooks & helpers
import characterComputedValues from '@/hooks/characterComputedValues'
import useCharacters from '@/hooks/useCharacters'

// Components
import Character from '@/components/Character'
import CardGroup from '@/components/CardGroup'
import FeatureCard from '@/components/cards/FeatureCard'
import ActionCard from '@/components/cards/ActionCard'
import WeaponCard from '@/components/cards/WeaponCard'
import EquipmentCard from '@/components/cards/EquipmentCard'
import SpellCard from '@/components/cards/SpellCard'

// Data
import classList from '@/data/classes'
import featureList from '@/data/features'
import raceList from '@/data/races'
import skillList from '@/data/skills'
import weaponList from '@/data/weapons'
import difficultyList from '@/data/difficulties'
import equipmentList from '@/data/equipment'
import spellList from '@/data/spells'

export default defineComponent({
  components: {
    Character,
    CardGroup,
    FeatureCard,
    ActionCard,
    WeaponCard,
    EquipmentCard,
    SpellCard
  },

  setup () {
    // State

    const router = useRouter()
    const currentSection = ref(0)
    const difficulty = 'beginner' // TODO, route
    const difficultyObj = difficultyList[difficulty]

    watch(currentSection, () => {
      window.scrollTo(0, 0)
    })

    // Character

    const { addCharacter } = useCharacters()

    const character = reactive({
      id: uniqid(),
      name: '',
      race: '',
      className: 'adventurer',
      difficulty: 'beginner',
      health: 0,
      spellPoints: 0,
      skills: [],
      features: [],
      spells: [],
      weapons: [],
      equipment: [],
      equippedArmor: [],
      inventory: {},
      restedAbilitiesUsed: {},
      activeEffects: [],
      gold: difficultyObj.startingGold,
      notes: ''
    })
    watch(character.equipment, (list) => {
      const inventory = {}
      list.forEach((key) => {
        const equipment = equipmentList[key]
        if (equipment.consumable) inventory[key] = equipment.consumable
      })
      character.inventory = inventory
    })
    provide('character', character)

    const computedCharacter = characterComputedValues(character)
    provide('computedCharacter', computedCharacter)

    const skillChoices = computed(() => {
      return Object.keys(skillList).reduce((acc, skill) => {
        if (acc[skill]) return acc
        const racialSkill = computedCharacter.race.value?.skillsIncluded?.includes(skill)
        const classSkill = computedCharacter.characterClass.value?.skillChoices?.includes(skill)
        if ((racialSkill || classSkill) && !acc[skill]) {
          if (racialSkill) {
            acc[skill] = {
              ...skillList[skill],
              included: true
            }
          } else {
            acc[skill] = skillList[skill]
          }
        }
        return acc
      }, {})
    })

    const mainHandRef = ref(null)
    const offHandRef = ref(null)
    const armorRef = ref(null)

    const mainHandWeaponIsTwoHand = computed(() => {
      const weaponKey = character.weapons[0]
      if (!weaponKey) return false
      const weapon = weaponList[weaponKey]
      return weapon.tags.includes('twoHand') && !weapon.tags.includes('oneHand')
    })
    watch(mainHandRef, (mainHand) => {
      if (!mainHand) return
      const weapon = weaponList[mainHand]
      offHandRef.value = ((weapon.tags.includes('twoHand') && !weapon.tags.includes('oneHand')) ? null : offHandRef.value)
    })

    const computedWeapons = computed(() => ([ mainHandRef.value, offHandRef.value ].filter((weapon) => weapon !== null)))
    watch(computedWeapons, (newWeapons) => character.weapons = newWeapons)

    const spentGold = computed(() => {
      const spentOnWeapons = character.weapons.reduce((acc, key) => {
        return acc + weaponList[key].price
      }, 0)
      const spentOnEquipment = character.equipment.reduce((acc, key) => {
        return acc + (equipmentList[key].price * ((equipmentList[key].consumable && !equipmentList[key].charges) ? equipmentList[key].consumable : 1))
      }, 0)
      return spentOnWeapons + spentOnEquipment
    })
    watch(spentGold, ((totalSpent) => {
      character.gold = difficultyObj.startingGold - totalSpent
    }))

    // Methods/callbacks

    const addRemoveFeature = (add, feature) => {
      if (add) {
        character.features.push(feature)
      } else {
        const index = character.features.indexOf(feature)
        character.features.splice(index, 1)
      }
      character.features.sort()
    }

    const addRemoveItem = (add, item) => {
      if (add) {
        character.equipment.push(item)
      } else {
        const index = character.equipment.indexOf(item)
        character.equipment.splice(index, 1)
      }
      character.equipment.sort()
    }

    const addRemoveArmor = (add, item) => {
      armorRef.value = add ? item : null
      addRemoveItem(add, item)
    }

    const addRemoveSpell = (add, spell) => {
      if (add) {
        character.spells.push(spell)
      } else {
        const index = character.spells.indexOf(spell)
        character.spells.splice(index, 1)
      }
      character.spells.sort()
    }

    const createCharacter = () => {
      character.health = computedCharacter.maxHealth.value
      character.spellPoints = computedCharacter.characterClass.value.spellPoints
      character.equippedArmor = [
        ...character.weapons,
        ...character.equipment
      ]
        .map((key) => ({ armor: (weaponList[key] || equipmentList[key]).armor, key }))
        .filter((item) => item.armor)
        .map((item) => item.key)
      addCharacter(character)
      const skillsIncluded = raceList[character.race].skillsIncluded
      if (skillsIncluded) {
        skillsIncluded.forEach((skill) => {
          if (!character.skills.includes(skill)) character.skills.push(skill)
        })
      }

      router.push({ name: 'playCharacter', params: { id: character.id } })
    }

    // Helpers

    const numFeaturesSelected = (list) => {
      return character.features.filter((feature) => list.includes(feature)).length
    }

    const numSpellsSelected = (list) => {
      return character.spells.filter((spell) => list.includes(spell)).length
    }

    const numTotalRaceFeatures = computed(() => {
      return (computedCharacter.race.value.featureChoices || []).reduce((acc, group) => acc + group.numChoices, 0)
    })

    const numTotalClassFeatures = computed(() => {
      return (computedCharacter.characterClass.value.featureChoices || []).reduce((acc, group) => acc + group.numChoices, 0)
    })

    const numTotalSpells = computed(() => {
      return Object.keys((computedCharacter.characterClass.value.spellChoices || {})).reduce((acc, key) => {
        const group = computedCharacter.characterClass.value.spellChoices[key]
        return acc + group.numChoices
      }, 0)
    })

    const hasSpells = computed(() => (
      (computedCharacter.characterClass.value.spells?.length) || 
      (
        computedCharacter.characterClass.value.spellChoices && 
        Object.keys(computedCharacter.characterClass.value.spellChoices).length
      )
    ))

    const calcPrice = (item) => {
      let price = item.price
      if (!item.charges && item.consumable) price *= item.consumable
      return price
    }

    const onReturnPressed = (evt, condition, then) => {
      if (!condition) return
      const key = evt.key.toLowerCase()
      if (key === 'return' || key === 'enter') then()
    }

    return {
      // Methods/callbacks
      addRemoveFeature,
      addRemoveItem,
      addRemoveArmor,
      addRemoveSpell,
      createCharacter,
      calcPrice,
      onReturnPressed,

      // Helpers
      numFeaturesSelected,
      numSpellsSelected,
      numTotalRaceFeatures,
      numTotalClassFeatures,
      numTotalSpells,
      hasSpells,

      // Character
      character,
      computed: computedCharacter,
      ...computedCharacter,
      skillChoices,
      mainHandWeaponIsTwoHand,
      mainHandRef,
      offHandRef,
      armorRef,

      // Other state
      currentSection,

      // Data
      classList,
      featureList,
      raceList,
      weaponList,
      equipmentList,
      spellList
    }
  }
})
</script>

<style lang="sass" scoped>
.sectionTitle
  font-family: 'Playfair Display', serif

.select
  &.is-wide
    width: 100%
    select 
      width: 100%

.checkbox
  span
    text-transform: uppercase

@media screen and (max-width: 1023px)
  .columns
    display: flex
    flex-direction: column
    .column
      &:first-child
        order: 1
      &:last-child
        order: 0
</style>
